import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, apiClient, type LoginInput } from "./api";

export const meQueryKey = ["me"] as const;

// The session lasts 30 days, so there is no need to re-check it often.
// networkMode "always": offline, the default mode would pause the query and
// leave the guard waiting forever; failing fast lets the app open from cache.
// retryOnMount false: after a network error the guard renders the shell,
// whose UserMenu also calls useMe; refetching on that mount would flip the
// query back to pending, unmount the shell and loop.
export function useMe() {
	return useQuery({
		queryKey: meQueryKey,
		queryFn: ({ signal }) => apiClient.me(signal),
		retry: false,
		retryOnMount: false,
		staleTime: 5 * 60_000,
		networkMode: "always",
	});
}

export function isUnauthorized(err: unknown): boolean {
	return err instanceof ApiError && err.status === 401;
}

export function useLogin() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (input: LoginInput) => apiClient.login(input),
		onSuccess: (me) => queryClient.setQueryData(meQueryKey, me),
	});
}

export function useLogout() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: () => apiClient.logout(),
		// Drop cached data even if the request fails: the user asked to leave.
		onSettled: () => queryClient.clear(),
	});
}

// safeNext keeps ?next= on this site, so a crafted link cannot bounce the
// user to another origin after login.
export function safeNext(next: string | null): string {
	if (!next || !next.startsWith("/") || next.startsWith("//") || next.startsWith("/\\")) {
		return "/today";
	}
	return next;
}
